import { useState } from "react";
import { useTranslation } from "react-i18next";
import { sendForgotPassword } from "../../api/Services";

import { Button, Input, message, Typography, Space, Alert } from "antd";
import { MailOutlined, CheckCircleOutlined, ArrowLeftOutlined } from "@ant-design/icons";

import styles from "./ForgotPassword.module.scss";

const { Title, Text } = Typography;

const ForgotPassword = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const sendEmail = async () => {
    setLoading(true);
    setError("");

    if (!email || !email.includes("@")) {
      setError(t("Common-InvalidEmail"));
      setLoading(false);
      return;
    }

    try {
      const response = await sendForgotPassword(email);
      if (response) {
        setSuccess(true);
        message.success(t("ForgotPassword-SendSuccesMessage"));
      } else {
        setError(t("ForgotPassword-SendErrorMessage"));
      }
    } catch (err) {
      setError(t("ForgotPassword-SendErrorMessage"));
    }

    setLoading(false);
  };

  const handleEmailChange = (event: any) => {
    setEmail(event.target.value);
    if (error) setError(""); // Clear error when typing
  };

  if (success) {
    return (
      <div className={styles.container}>
        <div className={styles.formWrapper}>
          <div className={styles.header}>
            <CheckCircleOutlined className={styles.successIcon} />
            <Title level={2} className={styles.title}>
              {t("ForgotPassword-SuccessTitle")}
            </Title>
            <Text type="secondary" className={styles.subtitle}>
              {t("ForgotPassword-SuccessMessage")}
            </Text>
          </div>
          <div className={styles.instructionsMessage}>
            <Text type="secondary">{t("ForgotPassword-CheckEmailMessage")}</Text>
          </div>
          <div className={styles.backToLogin}>
            <Button 
              type="link" 
              href="/" 
              className={styles.linkButton}
              icon={<ArrowLeftOutlined />}
            >
              {t("Common-ReturnLogin")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.formWrapper}>
        <div className={styles.header}>
          <MailOutlined className={styles.mailIcon} />
          <Title level={2} className={styles.title}>
            {t("ForgotPassword-Title")}
          </Title>
          <Text type="secondary" className={styles.subtitle}>
            {t("ForgotPassword-Description")}
          </Text>
        </div>

        {error && (
          <Alert
            message={t("Common-Error")}
            description={error}
            type="error"
            showIcon
            className={styles.errorAlert}
            closable
            onClose={() => setError("")}
          />
        )}

        <Space direction="vertical" size="large" className={styles.fullWidth}>
          <div className={styles.formItem}>
            <Text strong className={styles.label}>{t("Common-Email")}</Text>
            <Input
              className={styles.input}
              placeholder={t("Common-Email")}
              type="email"
              value={email}
              onChange={handleEmailChange}
              size="large"
              prefix={<MailOutlined className={styles.inputIcon} />}
            />
          </div>

          <Button
            onClick={sendEmail}
            type="primary"
            className={styles.submitButton}
            disabled={loading}
            loading={loading}
            icon={<MailOutlined />}
            size="large"
            block
          >
            {t("ForgotPassword-SendEmail")}
          </Button>

          <div className={styles.backToLogin}>
            <Button 
              type="link" 
              href="/" 
              className={styles.linkButton}
              icon={<ArrowLeftOutlined />}
            >
              {t("Common-ReturnLogin")}
            </Button>
          </div>
        </Space>
      </div>
    </div>
  );
};

export default ForgotPassword;
